import { IconButton } from '../../components/IconButton';
import { IconArrowBack } from '../../components/QuirkIcons';
import { QuirkIonContent } from '../../components/QuirkIonContent';
import { QuirkIonPage } from '../../components/QuirkIonPage';
import { Header } from '../../components/Header';
import { useHistory } from 'react-router';
import { ChangeEvent, useRef, useState } from 'react';
import { IonDatetime, IonDatetimeButton, IonModal, type DatetimeCustomEvent } from '@ionic/react';
import styles from './AdminTelemetryReport.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import tableStyles from '../../styles/table.module.css';
import { formatLocalDate } from 'model';
import {
	TelemetryCountsByDayRow,
	TelemetryTargetsByDayRow,
	useGetTelemetryCountsByDayQuery,
	useGetTelemetryTargetsByDayQuery
} from '../../api/Telemetry';
import { DownloadCSVButton } from '../../components/DownloadCSVButton';
import { DateTime } from 'luxon';
import { Checkbox } from '../../components/Forms/Checkbox';

export interface TelemetryReportState {
	startDate: string;
	endDate: string;
	type: string;
}

const TelemetryTargetsByDay = ({ rows }: { rows: TelemetryTargetsByDayRow[] }): JSX.Element => {
	const tableRef = useRef<HTMLTableElement>(null);
	return (
		<>
			<div className={styles.downloadButtonContainer}>
				<DownloadCSVButton
					style={{ width: 200 }}
					tableRef={tableRef}
					label="Download Report"
					filename="Unique Telemetry Targets By Day"
				/>
			</div>
			<table className={[styles.telemetryReport, tableStyles].join(' ')} ref={tableRef}>
				<thead className={tableStyles.thead}>
					<tr className={tableStyles.trHead}>
						<th className={tableStyles.th}>Date</th>
						<th className={tableStyles.th}>Target</th>
						<th className={tableStyles.th}>Total</th>
					</tr>
				</thead>
				<tbody className={tableStyles.tbody}>
					{rows.map(({ date, target, total }) => (
						<tr className={tableStyles.tr}>
							<td className={tableStyles.td}>{formatLocalDate(date)}</td>
							<td className={tableStyles.td}>{target}</td>
							<td className={[tableStyles.td, styles.total].join(' ')}>{total}</td>
						</tr>
					))}
				</tbody>
			</table>
		</>
	);
};

const TelemetryCountsByDay = ({ rows }: { rows: TelemetryCountsByDayRow[] }): JSX.Element => {
	const tableRef = useRef<HTMLTableElement>(null);
	return (
		<>
			<div className={styles.downloadButtonContainer}>
				<DownloadCSVButton
					style={{ width: 200 }}
					tableRef={tableRef}
					label="Download Report"
					filename="Unique Telemetry Counts By Day"
				/>
			</div>
			<table className={[styles.telemetryReport, tableStyles].join(' ')} ref={tableRef}>
				<thead className={tableStyles.thead}>
					<tr className={tableStyles.trHead}>
						<th className={tableStyles.th}>Date</th>
						<th className={tableStyles.th}>Type</th>
						<th className={tableStyles.th}>Total</th>
					</tr>
				</thead>
				<tbody className={tableStyles.tbody}>
					{rows.map(({ date, type, total }) => (
						<tr className={tableStyles.tr}>
							<td className={tableStyles.td}>{formatLocalDate(date)}</td>
							<td className={tableStyles.td}>{type}</td>
							<td className={[tableStyles.td, styles.total].join(' ')}>{total}</td>
						</tr>
					))}
				</tbody>
			</table>
		</>
	);
};

export function AdminTelemetryReport(): JSX.Element {
	const title = 'Telemetry Report';
	const history = useHistory();
	const [distinctValues, setDistinctValues] = useState<boolean>(true);
	const [noAnon, setNoAnon] = useState<boolean>(true);

	const [{ startDate, endDate, type }, setState] = useState<TelemetryReportState>({
		startDate: DateTime.now().minus({ days: 7 }).toISO().substring(0, 10),
		endDate: DateTime.now().toISO().substring(0, 10),
		type: ''
	});
	const handleDateTimeChange = (event: DatetimeCustomEvent) => {
		const { id, value } = event.target;
		setState(current => ({
			...current,
			[id]: value,
			type: ''
		}));
	};
	const handleTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
		setState(current => ({
			...current,
			type: event.target.value
		}));
	};
	const countsByDay = useGetTelemetryCountsByDayQuery({
		startDate: startDate.substring(0, 10),
		endDate: endDate.substring(0, 10),
		distinct: distinctValues,
		noAnon
	});
	const targetsByType = useGetTelemetryTargetsByDayQuery(
		{
			startDate,
			endDate,
			type: type ?? '',
			distinct: distinctValues,
			noAnon
		},
		{ skip: type === '' || type === undefined }
	);
	const types = countsByDay.data?.reduce((accum, { type }) => accum.add(type), new Set<string>());
	return (
		<QuirkIonPage name={title}>
			<Header
				title={title}
				left={
					<IconButton
						icon={<IconArrowBack />}
						onClick={() => {
							history.go(-1);
						}}
					/>
				}
			/>
			<QuirkIonContent scroll={true}>
				<div className={styles.reportWrapper}>
					<div className={styles.dateContainer}>
						<IonDatetimeButton style={{ width: 200 }} datetime="startDate" />
						<IonDatetimeButton style={{ width: 200 }} datetime="endDate" />
					</div>
					<IonModal keepContentsMounted>
						<IonDatetime
							presentation="date"
							id="startDate"
							value={startDate}
							onIonChange={handleDateTimeChange}
						></IonDatetime>
					</IonModal>
					<IonModal keepContentsMounted>
						<IonDatetime
							presentation="date"
							id="endDate"
							value={endDate}
							onIonChange={handleDateTimeChange}
						></IonDatetime>
					</IonModal>

					{types !== undefined && (
						<div className={styles.telemetryReportFilter}>
							<label htmlFor="telemetry-filter">
								View Breakdown
								<select
									id="telemetry-filter"
									value={type}
									style={{ width: '100%' }}
									onChange={handleTypeChange}
								>
									<option value={''}>-</option>
									{Array.from(types).map(type => (
										<option value={type}>{type}</option>
									))}
								</select>
							</label>
							<Checkbox
								onClick={e => setDistinctValues(e.target.checked)}
								value={distinctValues}
								label="Distinct Counts"
							/>
							<Checkbox
								onClick={e => {
									setNoAnon(e.target.checked);
								}}
								value={noAnon}
								label="Remove Anonymous"
							/>
						</div>
					)}
					{(type === '' || type === undefined) && countsByDay.data !== undefined && (
						<TelemetryCountsByDay rows={countsByDay.data} />
					)}
					{type !== '' && type !== undefined && targetsByType.data !== undefined && (
						<TelemetryTargetsByDay rows={targetsByType.data} />
					)}
				</div>
			</QuirkIonContent>
		</QuirkIonPage>
	);
}
